import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { getTicketCallThread } from "../../service/CallCenterTickets";
import { addCallNote, syncCallRecording } from "../../modules/telephony/browser-phone/telephonyApi";
import CallRecordingPlayer from "./CallRecordingPlayer";
import { CallStatus } from "../../service/CallCenterApi";

const formatThreadDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString();
};

const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "";
  const total = Math.max(0, Math.round(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const isCompletedCall = (status) => String(status || "").toUpperCase() === CallStatus.COMPLETED;

const TicketCallThread = ({ ticketId, pendingRecordingCallId = null }) => {
  const t = useCallCenterT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftNotes, setDraftNotes] = useState({});
  const [savingCallId, setSavingCallId] = useState(null);

  const loadThread = useCallback(async () => {
    if (!ticketId) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const response = await getTicketCallThread(ticketId);
      setItems(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error(error?.message || t("ccCallThreadLoadFailed"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  useEffect(() => {
    if (!pendingRecordingCallId || !ticketId) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 24;

    const poll = async () => {
      if (cancelled) {
        return;
      }
      attempts += 1;
      try {
        const synced = await syncCallRecording(pendingRecordingCallId);
        if (String(synced?.recordingUrl || "").trim()) {
          await loadThread();
          return;
        }
      } catch {
        // Best-effort fallback when SSE is delayed or unavailable.
      }

      try {
        const response = await getTicketCallThread(ticketId);
        if (cancelled) {
          return;
        }
        const list = Array.isArray(response) ? response : [];
        setItems(list);
        const pendingCall = list.find((item) => item?.call?.id === pendingRecordingCallId);
        if (pendingCall?.call?.recordingUrl || attempts >= maxAttempts) {
          return;
        }
      } catch {
        // Ignore transient poll failures; SSE or a later poll may succeed.
      }

      if (!cancelled && attempts < maxAttempts) {
        window.setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [pendingRecordingCallId, ticketId, loadThread]);

  const handleAddNote = async (callId) => {
    const note = String(draftNotes[callId] || "").trim();
    if (!note) return;
    try {
      setSavingCallId(callId);
      await addCallNote(callId, { ticketId, note });
      setDraftNotes((prev) => ({ ...prev, [callId]: "" }));
      toast.success(t("ccCommentAdded"));
      await loadThread();
    } catch (error) {
      toast.error(error?.message || t("ccCommentAddFailed"));
    } finally {
      setSavingCallId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">{t("ccLoading")}</p>;
  }

  if (!items.length) {
    return <p className="text-sm text-gray-500">{t("ccNoCallsOnTicket")}</p>;
  }

  return (
    <div className="space-y-4">
      {items.map((item) => {
        const call = item?.call || {};
        const callId = call.id;
        const directionLabel =
          call.direction === "INBOUND" ? t("ccCallDirectionInbound") : t("ccCallDirectionOutbound");
        const showRecordingPending =
          pendingRecordingCallId &&
          callId === pendingRecordingCallId &&
          isCompletedCall(call.status) &&
          !String(call.recordingUrl || "").trim();

        return (
          <div
            key={callId || `${call.createdAt}-${call.phoneNumber}`}
            className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  {directionLabel} · {call.phoneNumber || t("ccEmDash")}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t("ccCallAt")}: {formatThreadDateTime(call.startTime || call.createdAt)}
                  {call.duration != null ? ` · ${t("ccDuration")}: ${formatDuration(call.duration)}` : ""}
                </div>
                {item.agentName ? (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("ccCallAgent")}: {item.agentName}
                  </div>
                ) : null}
              </div>
              <span className="inline-flex items-center rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">
                {call.status || t("ccEmDash")}
              </span>
            </div>

            {showRecordingPending ? (
              <p className="text-xs text-gray-500 italic">{t("ccRecordingProcessing")}</p>
            ) : null}
            <CallRecordingPlayer callId={callId} recordingUrl={call.recordingUrl} />

            <div className="space-y-2">
              {(item.notes || []).length ? (
                item.notes.map((note) => (
                  <div key={note.id} className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{note.note}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {note.agentName || t("ccEmDash")} · {formatThreadDateTime(note.createdAt)}
                      {note.aiGenerated ? ` · ${t("ccAiGenerated")}` : ""}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">{t("ccNoCommentsForCall")}</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={draftNotes[callId] || ""}
                onChange={(event) =>
                  setDraftNotes((prev) => ({ ...prev, [callId]: event.target.value }))
                }
                placeholder={t("ccCallCommentPlaceholder")}
                className="flex-1 h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm"
                disabled={savingCallId === callId}
              />
              <button
                type="button"
                onClick={() => handleAddNote(callId)}
                disabled={savingCallId === callId || !String(draftNotes[callId] || "").trim()}
                className="h-10 px-4 rounded-lg bg-[#236DB4] text-sm font-semibold text-white hover:bg-[#1d5a96] disabled:opacity-50"
              >
                {savingCallId === callId ? t("ccSavingComment") : t("ccAddCallComment")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TicketCallThread;
